import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  X,
  Send,
  ArrowLeft,
  Package,
  Truck,
  DollarSign,
  CheckCircle2,
  Plus,
  Trash2,
  Check,
  Mail,
  MessageSquare,
  Link2,
  Loader2,
  Box,
  Target,
  Radio,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * SUPPLY MANIFEST BUILDER
 * Contractor Overwatch Design System - Spec-Ops Theme
 * Dark Mode + Emerald for money + Amber for actions
 */
const LogisticsManifestBuilder = ({
  estimateId,
  estimateData,
  clientInfo,
  companySettings,
  onClose,
}) => {
  const [loading, setLoading] = useState(true);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [publicLink, setPublicLink] = useState(null);
  
  const [projectName, setProjectName] = useState(clientInfo?.project_name || "Project");
  const [projectAddress, setProjectAddress] = useState(clientInfo?.address || "");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [deliveryNotes, setDeliveryNotes] = useState("Standard delivery to job site. Ensure clear access for unloading.");
  const [materialCategories, setMaterialCategories] = useState([]);

  const groups = estimateData?.groups || [];

  useEffect(() => {
    if (groups && groups.length > 0) {
      const categories = groups.map(group => ({
        id: group.id || Date.now() + Math.random(),
        name: group.name || "Uncategorized",
        items: (group.items || []).map(item => ({
          id: item.id || Date.now() + Math.random(),
          name: item.name || item.description || "Item",
          quantity: parseFloat(item.quantity) || 1,
          unit: item.unit || "ea",
          unitCost: parseFloat(item.unitCost) || 0,
          supplier: "",
          leadTime: "1-3 days",
          ordered: false,
          delivered: false,
          notes: ""
        }))
      }));
      setMaterialCategories(categories);
    }
    setLoading(false);
  }, [groups]);

  const calculateTotals = useCallback(() => {
    let totalCost = 0, totalItems = 0, orderedItems = 0, deliveredItems = 0;
    materialCategories.forEach(category => {
      category.items.forEach(item => {
        totalItems++;
        totalCost += item.quantity * item.unitCost;
        if (item.ordered) orderedItems++;
        if (item.delivered) deliveredItems++;
      });
    });
    return { totalCost, totalItems, orderedItems, deliveredItems };
  }, [materialCategories]);

  const totals = calculateTotals();

  const handleEditItem = (categoryIdx, itemIdx, field, value) => {
    setMaterialCategories(prev => prev.map((cat, ci) => 
      ci === categoryIdx ? { ...cat, items: cat.items.map((item, ii) => 
        ii === itemIdx ? { ...item, [field]: field === 'quantity' || field === 'unitCost' ? parseFloat(value) || 0 : value } : item
      )} : cat
    ));
  };

  const handleToggleStatus = (categoryIdx, itemIdx, field) => {
    setMaterialCategories(prev => prev.map((cat, ci) => 
      ci === categoryIdx ? { ...cat, items: cat.items.map((item, ii) => 
        ii === itemIdx ? { ...item, [field]: !item[field] } : item
      )} : cat
    ));
  };

  const handleAddItem = (categoryIdx) => {
    setMaterialCategories(prev => prev.map((cat, ci) => 
      ci === categoryIdx ? { ...cat, items: [...cat.items, { id: Date.now(), name: "New Material", quantity: 1, unit: "ea", unitCost: 0, supplier: "", leadTime: "TBD", ordered: false, delivered: false, notes: "" }]} : cat
    ));
  };

  const handleDeleteItem = (categoryIdx, itemIdx) => {
    setMaterialCategories(prev => prev.map((cat, ci) => 
      ci === categoryIdx ? { ...cat, items: cat.items.filter((_, ii) => ii !== itemIdx) } : cat
    ));
  };

  const handleAddCategory = () => {
    setMaterialCategories(prev => [...prev, { id: Date.now(), name: "New Category", items: [] }]);
  };

  const handleEditCategoryName = (categoryIdx, value) => {
    setMaterialCategories(prev => prev.map((cat, ci) => ci === categoryIdx ? { ...cat, name: value } : cat));
  };

  const handleDeleteCategory = (categoryIdx) => {
    setMaterialCategories(prev => prev.filter((_, ci) => ci !== categoryIdx));
  };

  const handleCopyLink = async () => {
    if (!estimateId) return;
    setLinkLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/api/documents/share`, {
        document_type: 'logistics_manifest', estimate_id: estimateId,
        data: { materialCategories, deliveryDate, deliveryNotes }
      }, { withCredentials: true });
      const link = response.data.public_url;
      setPublicLink(link);
      await navigator.clipboard.writeText(link);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    } catch (err) { console.error('Error generating link:', err); }
    finally { setLinkLoading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="text-slate-400 text-lg">Loading supply manifest...</div>
      </div>
    );
  }

  const hasEstimateData = estimateId || (groups && groups.length > 0);
  
  if (!hasEstimateData) {
    return (
      <div className="min-h-screen pt-14 bg-slate-950">
        {/* Header */}
        <div className="fixed top-0 left-0 right-0 z-[200] px-6 py-3 flex items-center justify-between bg-slate-900 border-b border-slate-800 shadow-lg">
          <button onClick={onClose} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white transition-all font-medium border border-slate-700">
            <ArrowLeft className="h-4 w-4" />
            <span>← Exit</span>
          </button>
          <span className="text-emerald-400 font-bold uppercase tracking-wider">SUPPLY MANIFEST</span>
          <button onClick={onClose} className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all border border-red-500/30">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col items-center justify-center" style={{ minHeight: "calc(100vh - 60px)" }}>
          <div className="text-center p-8 rounded-lg bg-slate-900 border-2 border-dashed border-slate-700 max-w-md">
            <h2 className="text-xl font-bold text-white mb-4 uppercase tracking-wide">No Estimate Data</h2>
            <p className="text-slate-400 mb-6">Create an estimate first using the ESTIMATING COMMAND, then return here to build your Supply Manifest.</p>
            <button onClick={onClose} className="px-6 py-3 rounded font-bold text-white transition-all hover:scale-105 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 shadow-lg shadow-amber-500/20">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-y-auto pb-32 pt-14 bg-slate-950">
      {/* Fixed Header */}
      <div className="fixed top-0 left-0 right-0 z-[200] px-6 py-3 flex items-center justify-between bg-slate-900 border-b border-slate-800 shadow-lg">
        <button onClick={onClose} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white transition-all font-medium border border-slate-700">
          <ArrowLeft className="h-4 w-4" />
          <span>← Exit</span>
        </button>
        <div className="flex items-center gap-3">
          <span className="text-emerald-400 font-bold uppercase tracking-wider">SUPPLY MANIFEST</span>
          <span className="text-slate-600">|</span>
          <span className="text-slate-400">{projectName}</span>
        </div>
        <button onClick={onClose} className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all border border-red-500/30">
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Document Container */}
      <div className="mx-auto mt-8 max-w-5xl bg-slate-900 rounded-lg border border-slate-800 shadow-lg overflow-hidden">
        
        {/* Document Header */}
        <div className="px-8 py-6 border-b border-slate-800 bg-slate-900/50">
          <div className="flex items-center gap-3 mb-2">
            <Target className="h-5 w-5 text-emerald-400" />
            <p className="text-xs uppercase tracking-[4px] text-emerald-400 font-bold">SUPPLY MANIFEST</p>
          </div>
          <h1 className="text-3xl font-bold text-white uppercase tracking-wide">{projectName}</h1>
          <p className="text-slate-400 text-sm mt-1">{companySettings?.company_name || "Alpha Construction Pros"}</p>
          
          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-3 mt-6">
            <div className="p-3 rounded-lg bg-slate-800 border border-slate-700">
              <div className="flex justify-between items-center">
                <span className="text-xs text-slate-500 uppercase tracking-wider">Total Assets</span>
                <Box className="h-4 w-4 text-emerald-400" />
              </div>
              <p className="text-2xl font-mono font-bold text-emerald-400 mt-1">{totals.totalItems}</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-800 border border-slate-700">
              <div className="flex justify-between items-center">
                <span className="text-xs text-slate-500 uppercase tracking-wider">Requisitioned</span>
                <CheckCircle2 className="h-4 w-4 text-amber-400" />
              </div>
              <p className="text-2xl font-mono font-bold text-amber-400 mt-1">{totals.orderedItems}</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-800 border border-slate-700">
              <div className="flex justify-between items-center">
                <span className="text-xs text-slate-500 uppercase tracking-wider">Received</span>
                <Truck className="h-4 w-4 text-emerald-400" />
              </div>
              <p className="text-2xl font-mono font-bold text-emerald-400 mt-1">{totals.deliveredItems}</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-800 border border-slate-700">
              <div className="flex justify-between items-center">
                <span className="text-xs text-slate-500 uppercase tracking-wider">Est. Cost</span>
                <DollarSign className="h-4 w-4 text-emerald-400" />
              </div>
              <p className="text-2xl font-mono font-bold text-emerald-400 mt-1">${totals.totalCost.toLocaleString('en-US', { minimumFractionDigits: 0 })}</p>
            </div>
          </div>
        </div>

        {/* Delivery Intelligence */}
        <div className="px-8 py-6 border-b border-slate-800">
          <div className="flex items-center gap-2 mb-4">
            <Radio className="h-4 w-4 text-emerald-400" />
            <p className="text-xs text-slate-500 uppercase tracking-widest font-bold">DELIVERY INTELLIGENCE</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
              <p className="text-xs text-slate-500 uppercase tracking-widest mb-2">DELIVERY LOCATION</p>
              <input type="text" value={projectAddress} onChange={(e) => setProjectAddress(e.target.value)} placeholder="Enter delivery address" className="w-full bg-transparent text-white font-medium focus:outline-none border-b border-slate-700 hover:border-slate-600 focus:border-emerald-500 placeholder-slate-600" />
            </div>
            <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
              <p className="text-xs text-slate-500 uppercase tracking-widest mb-2">TARGET DELIVERY DATE</p>
              <input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="w-full bg-transparent text-white font-medium focus:outline-none border-b border-slate-700 hover:border-slate-600 focus:border-emerald-500" />
            </div>
          </div>
          <div className="mt-4 p-4 rounded-lg bg-slate-800/50 border border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-widest mb-2">DELIVERY NOTES</p>
            <input type="text" value={deliveryNotes} onChange={(e) => setDeliveryNotes(e.target.value)} className="w-full bg-transparent text-slate-300 text-sm focus:outline-none border-b border-slate-700 hover:border-slate-600 focus:border-emerald-500" />
          </div>
        </div>

        {/* Asset Categories */}
        <div className="px-8 py-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-emerald-400" />
              <p className="text-xs text-slate-500 uppercase tracking-widest font-bold">ASSET CATEGORIES</p>
            </div>
            <button onClick={handleAddCategory} className="flex items-center gap-1 px-4 py-2 rounded font-bold text-white text-sm bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 shadow-lg shadow-amber-500/20 transition-all">
              <Plus className="h-4 w-4" /> Add Category
            </button>
          </div>

          <div className="space-y-6">
            {materialCategories.map((category, categoryIdx) => (
              <div key={category.id} className="rounded-lg overflow-hidden bg-slate-800/30 border border-slate-700">
                <div className="px-5 py-3 flex items-center justify-between bg-slate-800 border-b border-slate-700">
                  <input type="text" value={category.name} onChange={(e) => handleEditCategoryName(categoryIdx, e.target.value)} className="bg-transparent text-white font-bold uppercase tracking-wide focus:outline-none border-b border-transparent hover:border-slate-600 focus:border-emerald-500" />
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleAddItem(categoryIdx)} className="text-xs px-2 py-1 rounded text-emerald-400 hover:bg-emerald-500/10 font-medium">+ Add Item</button>
                    <button onClick={() => handleDeleteCategory(categoryIdx)} className="p-1 text-slate-500 hover:text-red-400 rounded hover:bg-red-500/10">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-slate-500 uppercase tracking-wider bg-slate-800/50">
                        <th className="px-4 py-3 w-8">ORD</th>
                        <th className="px-4 py-3 w-8">RCV</th>
                        <th className="px-4 py-3">ASSET</th>
                        <th className="px-4 py-3 w-20">QTY</th>
                        <th className="px-4 py-3 w-16">UNIT</th>
                        <th className="px-4 py-3 w-24">COST</th>
                        <th className="px-4 py-3 w-32">VENDOR</th>
                        <th className="px-4 py-3 w-24">ETA</th>
                        <th className="px-4 py-3 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {category.items.map((item, itemIdx) => (
                        <tr key={item.id} className="border-t border-slate-800 hover:bg-slate-800/30">
                          <td className="px-4 py-3">
                            <button onClick={() => handleToggleStatus(categoryIdx, itemIdx, 'ordered')} className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${item.ordered ? 'bg-amber-500 border-amber-500 text-white' : 'border-slate-600 text-transparent hover:border-amber-500'}`}>
                              <Check className="h-3 w-3" />
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <button onClick={() => handleToggleStatus(categoryIdx, itemIdx, 'delivered')} className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${item.delivered ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-600 text-transparent hover:border-emerald-500'}`}>
                              <Check className="h-3 w-3" />
                            </button>
                          </td>
                          <td className="px-4 py-3"><input type="text" value={item.name} onChange={(e) => handleEditItem(categoryIdx, itemIdx, 'name', e.target.value)} className="w-full bg-transparent text-white focus:outline-none border-b border-transparent hover:border-slate-600 focus:border-emerald-500" /></td>
                          <td className="px-4 py-3"><input type="number" value={item.quantity} onChange={(e) => handleEditItem(categoryIdx, itemIdx, 'quantity', e.target.value)} className="w-full bg-transparent text-slate-300 font-mono text-center focus:outline-none border-b border-slate-700 focus:border-emerald-500" /></td>
                          <td className="px-4 py-3"><input type="text" value={item.unit} onChange={(e) => handleEditItem(categoryIdx, itemIdx, 'unit', e.target.value)} className="w-full bg-transparent text-slate-400 text-center focus:outline-none border-b border-transparent hover:border-slate-600 focus:border-emerald-500" /></td>
                          <td className="px-4 py-3"><div className="flex items-center"><span className="text-slate-500">$</span><input type="number" value={item.unitCost} onChange={(e) => handleEditItem(categoryIdx, itemIdx, 'unitCost', e.target.value)} className="w-full bg-transparent text-slate-300 font-mono focus:outline-none border-b border-slate-700 focus:border-emerald-500" /></div></td>
                          <td className="px-4 py-3"><input type="text" value={item.supplier} onChange={(e) => handleEditItem(categoryIdx, itemIdx, 'supplier', e.target.value)} placeholder="Vendor" className="w-full bg-transparent text-slate-300 focus:outline-none border-b border-transparent hover:border-slate-600 focus:border-emerald-500 placeholder-slate-600" /></td>
                          <td className="px-4 py-3"><input type="text" value={item.leadTime} onChange={(e) => handleEditItem(categoryIdx, itemIdx, 'leadTime', e.target.value)} className="w-full bg-transparent text-slate-400 text-center focus:outline-none border-b border-transparent hover:border-slate-600 focus:border-emerald-500" /></td>
                          <td className="px-4 py-3"><button onClick={() => handleDeleteItem(categoryIdx, itemIdx)} className="p-1 text-slate-600 hover:text-red-400 rounded"><X className="h-4 w-4" /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary Footer */}
        <div className="px-8 py-6 bg-slate-800/50 border-t border-slate-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-500 text-sm">TOTAL CATEGORIES: <span className="text-white font-mono">{materialCategories.length}</span></p>
              <p className="text-slate-500 text-sm">ASSET STATUS: <span className="text-white font-mono">{totals.deliveredItems}/{totals.totalItems}</span> received</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500 uppercase tracking-wider">ESTIMATED PROCUREMENT COST</p>
              <p className="text-3xl font-mono font-bold text-emerald-400">${totals.totalCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Footer Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-[200] px-6 py-4 bg-slate-900/95 border-t border-slate-800 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <button onClick={onClose} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-all border border-slate-700">
            <ArrowLeft className="h-4 w-4" />
            <span className="font-medium">← Back</span>
          </button>
          <div className="flex items-center gap-3">
            <button onClick={handleCopyLink} disabled={linkLoading || !estimateId} className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700">
              {linkLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : linkCopied ? <Check className="h-4 w-4 text-emerald-400" /> : <Link2 className="h-4 w-4" />}
              {linkLoading ? "Preparing..." : linkCopied ? "Link Copied!" : "Copy Link"}
            </button>
            <button onClick={() => setSendModalOpen(true)} className="flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold transition-all hover:scale-105 text-white bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 shadow-lg shadow-amber-500/20">
              <Send className="h-4 w-4" />
              Send Manifest
            </button>
          </div>
        </div>
      </div>

      {/* Send Modal */}
      {sendModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center p-4 z-[9999] bg-black/85 backdrop-blur-sm" onClick={(e) => { if (e.target === e.currentTarget) setSendModalOpen(false); }}>
          <div className="w-full max-w-md rounded-lg p-6 bg-slate-900 border border-slate-800 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-white uppercase tracking-wide">Send Supply Manifest</h3>
              <button onClick={() => setSendModalOpen(false)} className="p-1 rounded hover:bg-slate-800 text-slate-400"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3">
              <button onClick={() => setSendModalOpen(false)} className="w-full flex items-center gap-3 p-4 rounded-lg hover:bg-slate-800 transition-all border border-slate-700">
                <Mail className="h-5 w-5 text-slate-400" />
                <div className="text-left"><p className="font-medium text-white">Send via Email</p><p className="text-sm text-slate-500">{clientInfo?.email || "Enter email address"}</p></div>
              </button>
              <button onClick={() => setSendModalOpen(false)} className="w-full flex items-center gap-3 p-4 rounded-lg hover:bg-slate-800 transition-all border border-slate-700">
                <MessageSquare className="h-5 w-5 text-slate-400" />
                <div className="text-left"><p className="font-medium text-white">Send via SMS</p><p className="text-sm text-slate-500">{clientInfo?.phone || "Enter phone number"}</p></div>
              </button>
              <button onClick={handleCopyLink} disabled={linkLoading || !estimateId} className="w-full flex items-center gap-3 p-4 rounded-lg hover:bg-slate-800 transition-all border border-slate-700">
                {linkLoading ? <Loader2 className="h-5 w-5 text-slate-400 animate-spin" /> : linkCopied ? <Check className="h-5 w-5 text-emerald-400" /> : <Link2 className="h-5 w-5 text-slate-400" />}
                <div className="text-left"><p className="font-medium text-white">{linkCopied ? "Link Copied!" : "Copy Shareable Link"}</p><p className="text-sm text-slate-500">{!estimateId ? "An estimate must be selected" : "Share a direct link to this document"}</p></div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogisticsManifestBuilder;
